/**
 * Indicador de Progreso por Fases
 * Muestra la carga progresiva del dashboard por prioridad
 * Se oculta automáticamente con fade out cuando llega al 100%
 * FLOTANTE y ARRASTRABLE - El usuario puede moverlo a cualquier posición
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  CheckCircle, Circle, Spinner, Briefcase, ChartLine, 
  Newspaper, Calculator, Bell, Link, CalendarBlank, Rewind,
  DotsSixVertical, X, ArrowsOutCardinal
} from '@phosphor-icons/react';

// Iconos para cada sección
const SECTION_ICONS = {
  markets: Briefcase,
  chart: ChartLine,
  portfolio: Briefcase,
  indicators: Calculator,
  news: Newspaper,
  tools: Calculator,
  alerts: Bell,
  correlations: Link,
  dividends: CalendarBlank,
  backtesting: Rewind,
};

// Componente de item de sección
const SectionItem = ({ section, status }) => {
  const Icon = SECTION_ICONS[section.id] || Circle;
  
  return (
    <div className={`flex items-center gap-1.5 transition-all duration-300 ${
      status === 'complete' ? 'opacity-100' : 
      status === 'loading' ? 'opacity-100' : 
      status === 'pending' ? 'opacity-60' : 'opacity-30'
    }`}>
      {status === 'complete' ? (
        <CheckCircle size={14} weight="fill" className="text-[#00ff6a] flex-shrink-0" />
      ) : status === 'loading' ? (
        <Spinner size={14} className="text-[#00dcff] animate-spin flex-shrink-0" />
      ) : (
        <Circle size={14} className="text-muted-foreground flex-shrink-0" />
      )}
      <span className={`text-[10px] font-medium whitespace-nowrap ${
        status === 'complete' ? 'text-[#00ff6a]' : 
        status === 'loading' ? 'text-[#00dcff]' : 'text-muted-foreground'
      }`}>
        {section.name}
      </span>
    </div>
  );
};

// Componente de fase
const PhaseIndicator = ({ phase, status, sections, getSectionStatus }) => {
  const isActive = status === 'loading';
  const isComplete = status === 'complete';
  
  return (
    <div className={`flex-1 p-3 rounded-lg border transition-all duration-300 ${
      isComplete ? 'bg-[#00ff6a]/10 border-[#00ff6a]/30' :
      isActive ? 'bg-[#00dcff]/10 border-[#00dcff]/50 shadow-[0_0_15px_rgba(0,220,255,0.2)]' :
      'bg-secondary/30 border-border/50'
    }`}>
      {/* Header de la fase */}
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
          isComplete ? 'bg-[#00ff6a] text-background' :
          isActive ? 'bg-[#00dcff] text-background animate-pulse' :
          'bg-secondary text-muted-foreground'
        }`}>
          {isComplete ? '✓' : phase.priority}
        </div>
        <div>
          <div className={`text-xs font-bold ${
            isComplete ? 'text-[#00ff6a]' :
            isActive ? 'text-[#00dcff]' :
            'text-muted-foreground'
          }`}>
            FASE {phase.priority}: {phase.name.toUpperCase()}
          </div>
          <div className="text-[10px] text-muted-foreground">
            {phase.description}
          </div>
        </div>
      </div>
      
      {/* Secciones de la fase */}
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {sections.map(section => (
          <SectionItem 
            key={section.id}
            section={section}
            status={getSectionStatus(section.id)}
          />
        ))}
      </div>
    </div>
  );
};

// Componente principal
export const PhaseLoadingProgress = ({ 
  currentPhase,
  completedPhases = [],
  progress = 0,
  ticker = 'NVDA',
  isVisible = true,
  getSectionStatus,
  LOADING_PHASES,
  SECTION_CONFIG,
  onReload, // Callback para recargar
  fastLoadMode = false, // Modo carga rápida
  onToggleFastLoad, // Toggle para modo rápido
}) => {
  // Estado para controlar el fade out
  const [shouldShow, setShouldShow] = useState(true);
  const [isHiding, setIsHiding] = useState(false);
  
  // Estado para el botón de recarga (aparece después de 30 segundos)
  const [showReloadButton, setShowReloadButton] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Estado para posición flotante y arrastre
  const [position, setPosition] = useState(() => {
    // Intentar cargar posición guardada
    try {
      const saved = localStorage.getItem('loadingProgressPosition');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {}
    // Posición por defecto: arriba centrado
    return { x: null, y: 20 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const containerRef = useRef(null);
  
  const allComplete = progress === 100;
  
  // Guardar posición en localStorage
  useEffect(() => {
    if (position.x !== null) {
      try {
        localStorage.setItem('loadingProgressPosition', JSON.stringify(position));
      } catch (e) {}
    }
  }, [position]);
  
  // Manejadores de arrastre
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('button')) return; // No arrastrar si se hace click en un botón
    
    e.preventDefault();
    setIsDragging(true);
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    }
  }, []);
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragOffset.current.x;
    const newY = e.clientY - dragOffset.current.y;
    
    // Limitar a los bordes de la ventana
    const maxX = window.innerWidth - (containerRef.current?.offsetWidth || 400);
    const maxY = window.innerHeight - (containerRef.current?.offsetHeight || 200);
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  }, [isDragging]);
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  // Event listeners globales para arrastre
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);
  
  // Contador de tiempo transcurrido
  useEffect(() => {
    if (!allComplete && isVisible) {
      const interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
      
      return () => clearInterval(interval);
    } else if (allComplete) {
      setElapsedTime(0);
      setShowReloadButton(false);
    }
  }, [allComplete, isVisible]);
  
  // Mostrar botón de recarga después de 30 segundos
  useEffect(() => {
    if (elapsedTime >= 30 && !allComplete) {
      setShowReloadButton(true);
    }
  }, [elapsedTime, allComplete]);
  
  // Efecto para ocultar con fade out cuando llegue al 100%
  useEffect(() => {
    if (allComplete && isVisible) {
      // Mostrar mensaje de "Listo" por 3 segundos, luego hacer fade out
      const fadeTimer = setTimeout(() => {
        setIsHiding(true);
      }, 3000);
      
      // Ocultar completamente después del fade out
      const hideTimer = setTimeout(() => {
        setShouldShow(false);
      }, 3700); // 3000ms delay + 700ms animación
      
      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    } else if (!allComplete) {
      // Si el progreso baja de 100 (nuevo ticker), mostrar de nuevo
      setShouldShow(true);
      setIsHiding(false);
    }
  }, [allComplete, isVisible]);
  
  // Handler para recargar
  const handleReload = () => {
    if (onReload) {
      onReload();
    } else {
      // Fallback: recargar página
      window.location.reload();
    }
  };
  
  // Resetear posición al centro
  const handleResetPosition = () => {
    setPosition({ x: null, y: 20 });
    localStorage.removeItem('loadingProgressPosition');
  };
  
  // No renderizar si está oculto o no es visible
  if (!isVisible || !shouldShow) return null;
  
  const getPhaseStatus = (phaseId) => {
    if (completedPhases.includes(phaseId)) return 'complete';
    if (currentPhase === phaseId) return 'loading';
    return 'waiting';
  };
  
  // Calcular estilo de posición
  const positionStyle = position.x !== null 
    ? { left: position.x, top: position.y }
    : { left: '50%', top: position.y, transform: 'translateX(-50%)' };
  
  // Estilo 3D para textos - sombra fuerte para legibilidad
  const text3DStyle = {
    textShadow: '0 0 8px rgba(0,0,0,1), 0 0 15px rgba(0,0,0,0.9), 0 2px 4px rgba(0,0,0,1), 0 0 20px rgba(0,255,106,0.3)'
  };
  
  const content = (
    <div 
      ref={containerRef}
      className={`fixed z-[100] transition-all duration-300 ease-out ${
        isHiding ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
      } ${isDragging ? 'cursor-grabbing' : ''}`}
      style={{
        ...positionStyle,
        maxWidth: isMinimized ? '280px' : '900px',
        width: isMinimized ? 'auto' : '95vw',
      }}
      data-testid="phase-loading-progress"
    >
      {/* Contenedor principal - 100% TRANSPARENTE con efecto 3D en textos */}
      <div 
        className="rounded-lg transition-all duration-500"
        style={{
          background: 'transparent',
          border: '2px solid #00dcff',
          boxShadow: `
            0 0 10px rgba(0, 220, 255, 0.8),
            0 0 20px rgba(0, 220, 255, 0.4),
            0 0 30px rgba(0, 220, 255, 0.2),
            inset 0 0 15px rgba(0, 220, 255, 0.1),
            0 4px 0 rgba(0, 80, 120, 0.8),
            0 6px 10px rgba(0, 0, 0, 0.5)
          `
        }}
      >
        {/* Header con arrastre */}
        <div 
          className="flex items-center justify-between px-4 py-2 cursor-grab active:cursor-grabbing"
          onMouseDown={handleMouseDown}
        >
          {/* Lado izquierdo: indicador + ticker + tiempo */}
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${
              allComplete 
                ? 'bg-[#00ff6a] shadow-[0_0_15px_#00ff6a,0_0_30px_#00ff6a]' 
                : 'bg-[#00ff6a] animate-pulse shadow-[0_0_15px_#00ff6a,0_0_25px_#00ff6a]'
            }`} />
            <span 
              className="text-xl font-black text-[#00ff6a] tracking-wide"
              style={{ 
                textShadow: '0 0 12px rgba(0,255,106,1), 0 0 25px rgba(0,255,106,0.8), 0 0 35px rgba(0,255,106,0.5), 0 2px 4px rgba(0,0,0,1)'
              }}
            >
              {allComplete ? '✓ 100% LISTO' : `CARGANDO ${ticker}...`}
            </span>
            {!allComplete && elapsedTime > 0 && (
              <div 
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 transition-all duration-500 ${
                  elapsedTime >= 10 
                    ? 'bg-gold-500/20 border-gold-400/60 shadow-[0_0_25px_rgba(245,158,11,0.5)]' 
                    : 'bg-[#00ff6a]/10 border-[#00ff6a]/40'
                }`}
                style={{
                  animation: elapsedTime >= 10 ? 'heartbeat 1s ease-in-out infinite' : 'none'
                }}
              >
                <span 
                  className={`text-4xl font-mono font-black tracking-tight tabular-nums ${
                    elapsedTime >= 10 ? 'text-gold-400' : 'text-[#00ff6a]'
                  }`}
                  style={{ 
                    textShadow: elapsedTime >= 10 
                      ? '0 0 20px rgba(245,158,11,1), 0 0 40px rgba(245,158,11,0.8), 0 0 60px rgba(245,158,11,0.5), 0 2px 4px rgba(0,0,0,1)'
                      : '0 0 15px rgba(0,255,106,1), 0 0 30px rgba(0,255,106,0.8), 0 0 45px rgba(0,255,106,0.5), 0 2px 4px rgba(0,0,0,1)',
                    animation: elapsedTime >= 10 ? 'countPulse 0.5s ease-in-out infinite' : 'pulse 1.5s ease-in-out infinite'
                  }}
                >
                  {elapsedTime}
                </span>
                <div className="flex flex-col">
                  <span 
                    className={`text-lg font-black ${elapsedTime >= 10 ? 'text-gold-400' : 'text-[#00ff6a]/80'}`}
                    style={text3DStyle}
                  >
                    seg
                  </span>
                  {elapsedTime >= 10 && (
                    <span 
                      className="text-[10px] font-bold text-gold-300 animate-pulse"
                      style={{ textShadow: '0 0 8px rgba(245,158,11,0.8)' }}
                    >
                      ⏳ espera...
                    </span>
                  )}
                </div>
              </div>
            )}
            {fastLoadMode && !allComplete && (
              <span 
                className="text-xs font-black text-[#00ff6a]"
                style={text3DStyle}
              >
                ⚡RÁPIDO
              </span>
            )}
          </div>
          
          {/* Lado derecho: controles + progreso */}
          <div className="flex items-center gap-3">
            {!allComplete && onToggleFastLoad && (
              <button
                onClick={onToggleFastLoad}
                className="px-2 py-0.5 text-xs font-bold text-[#00ff6a] hover:text-white transition-all"
                style={text3DStyle}
              >
                ⚡ {fastLoadMode ? 'ON' : 'Rápido'}
              </button>
            )}
            {currentPhase && !allComplete && (
              <div className="px-3 py-1 bg-gold-500/20 rounded-lg border border-gold-500/50">
                <span 
                  className="text-lg font-bold text-gold-400"
                  style={{ 
                    textShadow: '0 0 10px rgba(0,212,255,1), 0 0 20px rgba(0,212,255,0.8), 0 2px 4px rgba(0,0,0,1)'
                  }}
                >
                  Fase {currentPhase === 'phase1' ? '1' : currentPhase === 'phase2' ? '2' : '3'}/3
                </span>
              </div>
            )}
            <span 
              className="text-4xl font-mono font-black text-[#00ff6a] tabular-nums"
              style={{ 
                textShadow: '0 0 15px rgba(0,255,106,1), 0 0 30px rgba(0,255,106,0.8), 0 0 45px rgba(0,255,106,0.5), 0 2px 4px rgba(0,0,0,1)',
                letterSpacing: '-0.05em'
              }}
            >
              {progress}%
            </span>
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 text-[#00ff6a] hover:text-white transition-colors"
              style={text3DStyle}
            >
              <span className="text-sm font-bold">{isMinimized ? '▢' : '—'}</span>
            </button>
            <button
              onClick={handleResetPosition}
              className="p-1 text-[#00ff6a] hover:text-white transition-colors"
              style={text3DStyle}
            >
              <span className="text-sm font-bold">⌂</span>
            </button>
          </div>
        </div>
        
        {/* Barra de progreso - VERDE GANANCIAS */}
        <div 
          className="h-2 mx-4 my-2 rounded-full overflow-hidden"
          style={{
            background: 'rgba(0, 255, 106, 0.15)',
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.5)'
          }}
        >
          <div 
            className="h-full transition-all duration-500 ease-out rounded-full"
            style={{ 
              width: `${progress}%`,
              background: 'linear-gradient(90deg, #00ff6a, #00d26a, #00ff6a)',
              boxShadow: '0 0 15px rgba(0, 255, 106, 0.9), 0 0 30px rgba(0, 255, 106, 0.5), inset 0 1px 0 rgba(255,255,255,0.3)'
            }}
          />
        </div>
        
        {/* Contenido expandido */}
        {!isMinimized && (
          <div className="px-4 py-2">
            {/* Fases en línea horizontal */}
            <div className="flex items-center gap-3 text-xs">
              {Object.values(LOADING_PHASES)
                .filter(phase => !fastLoadMode || phase.id === 'phase1')
                .map((phase, idx, arr) => {
                  const status = getPhaseStatus(phase.id);
                  const isComplete = status === 'complete';
                  const isActive = status === 'loading';
                  
                  return (
                    <div key={phase.id} className="flex items-center gap-1.5">
                      {/* Icono de estado */}
                      {isComplete ? (
                        <span className="text-[#00ff6a] font-bold" style={text3DStyle}>✓</span>
                      ) : isActive ? (
                        <span className="text-[#00ff6a] animate-pulse font-bold" style={text3DStyle}>●</span>
                      ) : (
                        <span className="text-[#00ff6a]/40 font-bold" style={text3DStyle}>○</span>
                      )}
                      {/* Nombre */}
                      <span 
                        className={`font-bold ${
                          isComplete ? 'text-[#00ff6a]' : 
                          isActive ? 'text-[#00ff6a]' : 'text-[#00ff6a]/50'
                        }`} 
                        style={text3DStyle}
                      >
                        {phase.name.split(':')[0]}
                      </span>
                      {/* Separador */}
                      {idx < arr.length - 1 && (
                        <span className="text-[#00ff6a]/50 mx-1 font-bold" style={text3DStyle}>›</span>
                      )}
                    </div>
                  );
                })}
              
              {/* Mensaje de estado actual */}
              <span 
                className="text-[#00ff6a] ml-auto font-semibold"
                style={text3DStyle}
              >
                {allComplete ? '• Precios actualizándose' : 
                 currentPhase === 'phase1' ? '• Mercados, Gráfico, Portafolio' :
                 currentPhase === 'phase2' ? '• Indicadores, Noticias' : '• Alertas, Extras'}
              </span>
            </div>
            
            {/* Botón recargar si tarda mucho */}
            {showReloadButton && !allComplete && (
              <div className="flex items-center justify-between mt-2 pt-2">
                <span className="text-xs text-[#00ff6a] font-bold" style={text3DStyle}>
                  ⏱️ Tardando más de lo esperado
                </span>
                <button
                  onClick={handleReload}
                  className="px-3 py-1 bg-[#00ff6a] hover:bg-[#00d26a] text-black text-xs font-black rounded transition-all shadow-[0_0_15px_rgba(0,255,106,0.5)]"
                >
                  Recargar
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
  
  // Usar Portal para renderizar fuera del flujo del documento
  return createPortal(content, document.body);
};

export default PhaseLoadingProgress; 
